import React from "react";
import { FileText, Loader2, Smartphone } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import {
  EmptyState,
  formatDate,
  formatMoney,
  getStatusClasses,
  SectionCard,
  SummaryTile,
  useTenantPortalData,
} from "./tenantPortalShared.jsx";

export default function TenantInvoicesPage({ isDark, setIsDark, currency, setCurrency }) {
  const { user } = useAuth();
  const toast = useToast();
  const { invoices, invoiceSummary, paymentIntentLookup, handlePayNow, payingInvoiceId } = useTenantPortalData(user, toast);

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Resident Portal"
      title="My invoices"
      description="Invoice records issued for your tenancy."
    >
      <SectionCard
        title="Invoice summary"
        description="A clear view of billed, outstanding, and overdue invoice amounts."
        isDark={isDark}
      >
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <SummaryTile label="Invoices" value={invoiceSummary.count} isDark={isDark} />
          <SummaryTile label="Total billed" value={formatMoney(invoiceSummary.total, currency)} isDark={isDark} />
          <SummaryTile label="Outstanding" value={formatMoney(invoiceSummary.outstanding, currency)} isDark={isDark} />
          <SummaryTile label="Overdue" value={formatMoney(invoiceSummary.overdue, currency)} isDark={isDark} />
        </div>
      </SectionCard>

      <SectionCard
        title="Invoice history"
        description="Your latest tenancy invoices."
        isDark={isDark}
      >
        {invoices.length === 0 ? (
          <EmptyState
            title="No invoices yet"
            body="When invoices are issued for this tenancy, they will appear here."
            isDark={isDark}
          />
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <div
                key={invoice.id}
                className={`rounded-2xl border p-4 ${
                  isDark ? "border-slate-800 bg-slate-950" : "border-slate-200 bg-slate-50"
                }`}
              >
                {(() => {
                  const outstandingAmount = Number(invoice.balance_due ?? invoice.amount ?? 0);
                  const activeIntent = paymentIntentLookup.get(invoice.id);
                  const activeIntentStatus = String(activeIntent?.status || "").toLowerCase();
                  const paymentPending = ["pending", "submitted"].includes(activeIntentStatus);
                  const paymentSucceeded = activeIntentStatus === "success";
                  const canPayNow = invoice.status !== "Paid" && outstandingAmount > 0 && !paymentPending && !paymentSucceeded;

                  return (
                    <>
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <FileText className={`h-4 w-4 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
                            <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{invoice.id}</p>
                          </div>
                          <p className={`mt-1 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                            Issued {formatDate(invoice.issue_date)} | Due {formatDate(invoice.due_date)}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(invoice.status)}`}>
                            {invoice.status}
                          </span>
                          <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                            {invoice.delivery || "Draft"}
                          </span>
                          {paymentPending ? (
                            <span className="inline-flex rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                              Payment request pending
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className={`mt-3 flex flex-wrap gap-4 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                        <span>Amount: {formatMoney(invoice.amount, currency)}</span>
                        <span>Outstanding: {formatMoney(outstandingAmount, currency)}</span>
                        <span>Unit: {invoice.unit}</span>
                      </div>
                      {activeIntent?.external_reference ? (
                        <p className={`mt-3 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          Payment reference: {activeIntent.external_reference}
                        </p>
                      ) : null}
                      <div className="mt-4 flex flex-wrap items-center gap-3">
                        <button
                          type="button"
                          onClick={() => handlePayNow(invoice)}
                          disabled={!canPayNow || payingInvoiceId === invoice.id}
                          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
                            canPayNow && payingInvoiceId !== invoice.id
                              ? "bg-amber-500 text-white hover:bg-amber-600"
                              : "cursor-not-allowed bg-slate-200 text-slate-500"
                          }`}
                        >
                          {payingInvoiceId === invoice.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Smartphone className="h-4 w-4" />
                          )}
                          {paymentPending ? "Awaiting M-Pesa prompt" : invoice.status === "Paid" ? "Already paid" : "Pay now"}
                        </button>
                        <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          {paymentPending
                            ? "A payment request has already been created for this invoice."
                            : "Creates a payment request for this invoice and prepares the M-Pesa payment flow."}
                        </p>
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </AppLayout>
  );
}
